import React, { useState, useMemo, useCallback } from "react";
import {
  DataGrid,
  GridRowModes,
  GridActionsCellItem,
  GridRowEditStopReasons,
} from "@mui/x-data-grid";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import SaveIcon from "@mui/icons-material/Save";
import CancelIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";

import EditToolbar from "./EditToolbar";
import DeleteDialog from "./DeleteDialog";
import { useApp } from "../../AppProvider";
import { useDataAdminGrid } from "./useDataAdminGrid";
import { Alert, AlertTitle } from "@mui/material";

const StyledDataGrid = styled(DataGrid)({
  height: 600,
  "& .MuiDataGrid-row:hover": {
    cursor: "pointer",
  },
  "& .MuiDataGrid-row.Mui-selected": {
    backgroundColor: "#d9e7f5",
  },
  "& .MuiDataGrid-row.Mui-selected:hover": {
    backgroundColor: "#c3dbf2",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing": {
    backgroundColor: "#fff8dc",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing .MuiDataGrid-cell": {
    backgroundColor: "#fff8dc",
  },
  "& .MuiDataGrid-row.MuiDataGrid-row--editing:hover .MuiDataGrid-cell": {
    backgroundColor: "#ffeeba",
  },
});

export default function DataAdminGrid({
  baseUrl,
  columns,
  primaryKeyField = "id",
  allowEdit = true,
  allowDelete = true,
  allowAdd = true,
  rowFilter,
}) {
  const { doToast } = useApp();
  const { rows, isLoading, error, saveMutation, deleteMutation } =
    useDataAdminGrid(baseUrl, primaryKeyField);

  const [rowModesModel, setRowModesModel] = useState({});
  const [localRows, setLocalRows] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [rowIdToDelete, setRowIdToDelete] = useState(null);

  const allRows = useMemo(() => {
    const filterFn = typeof rowFilter === "function" ? rowFilter : () => true;

    const filteredServerRows = rows.filter(filterFn);
    const existingRowIds = new Set(filteredServerRows.map((r) => r.rowId));
    const filteredLocalRows = localRows.filter(filterFn);
    const newRows = filteredLocalRows.filter(
      (r) => !existingRowIds.has(r.rowId)
    );

    return [...newRows, ...filteredServerRows];
  }, [rows, localRows, rowFilter]);

  const handleRowEditStop = (params, event) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const handleRowModesModelChange = (newModel) => {
    setRowModesModel(newModel);
  };

  const handleEditClick = (rowId) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [rowId]: { mode: GridRowModes.Edit },
    }));
  };

  const handleSaveClick = (rowId) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [rowId]: { mode: GridRowModes.View },
    }));
  };

  const handleCancelClick = (rowId) => () => {
    setRowModesModel((prev) => ({
      ...prev,
      [rowId]: { mode: GridRowModes.View, ignoreModifications: true },
    }));
    setLocalRows((prev) => prev.filter((r) => r.rowId !== rowId));
  };

  const handleDeleteClick = useCallback(
    (rowId) => () => {
      if (!allowDelete) return;
      const row = allRows.find((r) => r.rowId === rowId);
      if (!row) return;
      const pkValue = row[primaryKeyField];
      if (pkValue == null) {
        setLocalRows((prev) => prev.filter((r) => r.rowId !== rowId));
      } else {
        setRowIdToDelete(rowId);
        setDeleteDialogOpen(true);
      }
    },
    [allRows, primaryKeyField, allowDelete]
  );

  const processRowUpdate = async (updatedRow) => {
    if (!allowEdit) {
      doToast("error", "Editing is disabled.");
      return updatedRow;
    }

    const payload = { ...updatedRow };
    delete payload.rowId;
    try {
      const saved = await saveMutation.mutateAsync(payload, {
        onSuccess: (data) => {
          doToast("success", data?.message || "Record saved successfully!");
        },
      });
      const newRowId = saved[primaryKeyField] ?? updatedRow.rowId;
      setLocalRows((prev) => prev.filter((r) => r.rowId !== updatedRow.rowId));
      return { ...saved, rowId: newRowId };
    } catch (err) {
      doToast("error", "An error occurred while saving the row.");
      throw err;
    }
  };

  // Combine grid columns with a dedicated "actions" column.
  const mergedColumns = useMemo(() => {
    const includeActions = allowEdit || allowDelete;

    const actionsColumn = includeActions
      ? {
          field: "actions",
          type: "actions",
          headerName: "Actions",
          width: 120,
          getActions: ({ row }) => {
            const rowId = row.rowId;
            const isInEditMode =
              rowModesModel[rowId]?.mode === GridRowModes.Edit;

            if (isInEditMode && allowEdit) {
              return [
                <GridActionsCellItem
                  key="save"
                  icon={<SaveIcon />}
                  label="Save"
                  onClick={handleSaveClick(rowId)}
                  color="primary"
                />,
                <GridActionsCellItem
                  key="cancel"
                  icon={<CancelIcon />}
                  label="Cancel"
                  onClick={handleCancelClick(rowId)}
                  color="inherit"
                />,
              ];
            }

            const actions = [];

            if (allowEdit) {
              actions.push(
                <GridActionsCellItem
                  key="edit"
                  icon={<EditIcon />}
                  label="Edit"
                  onClick={handleEditClick(rowId)}
                  color="inherit"
                />
              );
            }

            if (allowDelete) {
              actions.push(
                <GridActionsCellItem
                  key="delete"
                  icon={<DeleteIcon />}
                  label="Delete"
                  onClick={handleDeleteClick(rowId)}
                  color="inherit"
                />
              );
            }

            return actions;
          },
        }
      : null;

    return actionsColumn ? [actionsColumn, ...columns] : [...columns];
  }, [columns, rowModesModel, allowEdit, allowDelete, handleDeleteClick]);

  if (error)
    return (
      <Box sx={{ m: 2 }}>
        <Alert severity="error">
          <AlertTitle>Error loading data</AlertTitle>
          Please try again or contact support.
        </Alert>
      </Box>
    );

  return (
    <>
      <StyledDataGrid
        rows={allRows}
        columns={mergedColumns}
        getRowId={(row) => row.rowId}
        editMode="row"
        isCellEditable={() => allowEdit}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        loading={isLoading}
        slots={{
          toolbar: () => (
            <EditToolbar
              setRowModesModel={setRowModesModel}
              localRows={localRows}
              setLocalRows={setLocalRows}
              primaryKeyField={primaryKeyField}
              allowAdd={allowAdd}
            />
          ),
        }}
      />

      <DeleteDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          const row = allRows.find((r) => r.rowId === rowIdToDelete);
          if (row) {
            deleteMutation.mutate(row[primaryKeyField], {
              onSuccess: (data) => {
                doToast(
                  "success",
                  data?.message || "Record deleted successfully!"
                );
              },
            });
          }
          setDeleteDialogOpen(false);
          setRowIdToDelete(null);
        }}
      />
    </>
  );
}
